/**
 * The shape of a briefing, so it can be read rather than only received.
 *
 * Leo, 2026-09-16: *"briefings are not quite friendly to read, theyre just a block of
 * text, we need to make sure they are properly displayed, with paragraphs and
 * semantically correct structure so theyre easy to read, mine for example list a bunch of
 * points in a single line, that makes it really hard to read and would be much better as a
 * bulleted list."*
 *
 * **That is two faults and this file exists because fixing either one alone looks like a
 * fix and is not.** The chat rendered every message with `white-space: pre-wrap`, so a
 * briefing was one grey block whatever was in it — but the deeper half is that the
 * generation prompt said, in as many words, *"No headings, no bullet lists, no markdown"*,
 * so the model wrote a sentence containing six comma-separated points. No renderer in
 * existence turns that sentence into a list. The structure has to be decided where the
 * briefing is written, which means it has to be something the model is *asked* for and
 * something the wire can carry.
 *
 * ## What is carried, and why it is text
 *
 * The model returns **blocks** — the schema in `packages/ingest/src/items.ts` gives it
 * nothing else to return — and `projectBriefing` turns those into one string with a
 * deliberately tiny set of markers: a blank line between blocks, `- ` in front of a bullet,
 * `## ` in front of a heading, `1) ` in front of a step, and a pipe at each end of a table
 * row. That string is what is stored on the item, sent as the message, written to the log
 * and cut down for a push preview. `parseBriefing` reads it back into blocks for anything
 * that can render them.
 *
 * **Every marker has to read as itself where nothing can render it**, which is why they
 * look like this and not like sentinels. A briefing goes out by email when push cannot
 * reach the learner (F-122), and `| Vessel | Point |` and `1) Take the pulse` are a table
 * and a sequence to somebody reading plain text. That is the same bar `- ` and `## ` were
 * chosen against.
 *
 * **The stored string is the plain-text projection, by construction, and that is the whole
 * reason the format is text.** A briefing reaches a learner through the chat, through a
 * push preview (`previewOf` in `packages/messaging/src/push.ts`, which collapses it to one
 * line) and historically as plain text in a message body; only the first of those can
 * render structure. A second DynamoDB attribute holding the blocks would have to be
 * threaded through `sweep.ts`, `transcript.ts`, the push payload and the daily summary, it
 * would be absent on all 125 briefings already live, and every reader would still need the
 * text path as a fallback — so the structured field would buy nothing and cost four
 * producers. One field, and the markers read as themselves anywhere that cannot do better.
 *
 * ## What was rejected
 *
 * **A Markdown library.** `marked` and `markdown-it` both pass raw HTML through by default
 * and both are used with `dangerouslySetInnerHTML`, and a briefing is model output written
 * from documents the *learner* supplied — a PDF is an injection surface, and this is the
 * one place its text reaches a page. Nothing here produces HTML: `parseBriefing` returns
 * strings that React puts in text nodes, so a briefing containing `<script>` renders as
 * the characters `<script>` and there is no code path where it does anything else. **That
 * is what F-150 could add a table without reopening**: a cell is a string in the same
 * sense a bullet is, and the escaping that protects a paragraph protects a cell for the
 * same reason and by the same mechanism — see `renders a table cell as text` in
 * `packages/web/test/chat-prose.test.ts`, which asserts it against the real renderer.
 *
 * The subset is five constructs because five is what a 250–300 word briefing needs; bold,
 * links, images and code fences are deliberately absent and pass through as the literal
 * characters the model typed, which is ugly and never unsafe.
 *
 * **Prompting harder with no format.** That was the status quo. The instruction said plain
 * prose and the model complied perfectly; the complaint is about what plain prose does to a
 * list of six things.
 *
 * ## Legacy briefings
 *
 * Every briefing written before this file is plain prose, usually with blank lines between
 * paragraphs, and it parses into paragraph blocks and renders better than it did. One
 * written with `-` bullets by a model that ignored the old instruction now renders as a
 * list. Nothing blanks out: `parseBriefing` never returns an empty list for a non-empty
 * string, because a briefing that failed to parse must still be the thing the learner
 * reads.
 */
/**
 * The markers, exported because the generation prompt names them to the model.
 *
 * A prompt that described the markers in its own words would be a second definition of
 * this format, and the failure would be silent: the model writes `* ` and `parseBriefing`
 * reads a paragraph beginning with an asterisk.
 */
export const BRIEFING_BULLET = '- ';
export const BRIEFING_HEADING = '## ';
/**
 * A table row's fence, and it is at *both* ends of the line on purpose.
 *
 * A trailing pipe is what makes an empty last cell survive the round trip. `| a | ` has a
 * trailing space that `parseBriefing` trims away before it splits, so a two-column row
 * whose second cell is blank would come back as one cell — a table that silently loses a
 * column in exactly the case a comparison table has one, "this one has no equivalent".
 * With the fence at both ends the split is over the text *between* them and an empty cell
 * is a cell.
 */
export const BRIEFING_CELL = '|';
/**
 * A step's marker: `1) `, `2) `, and never `1. `.
 *
 * **The full stop is taken and taking it would cost a learner a lapse.** A multiple-choice
 * question arrives as `1. Taiyuan` / `2. Lieque` with *"Reply with the number"* under it
 * (`questionText` in `packages/messaging/src/choice.ts`), and those numbers are what the
 * learner types back. `BULLET_LINE` excludes digits for that reason and this format may
 * not undo it from the other side: a parser that read `1. ` as a step would turn every
 * choice question's options into an ordered list, renumbered by the renderer, and the
 * learner who reads "the third one" and types 3 is marked wrong.
 *
 * `1) ` collides with nothing this system sends and reads as a sequence anywhere the
 * markers are shown as themselves — an email body, a push preview.
 */
export const BRIEFING_STEP = ') ';
/** A heading line, at any of Markdown's six depths. Everything deeper is not a heading. */
const HEADING_LINE = /^#{1,6}[ \t]+(.*)$/;
/**
 * A bullet line.
 *
 * `-`, `*` and `•` and no numbers, and the exclusion of numbers is load-bearing rather
 * than an omission. A multiple-choice question arrives as `1. Taiyuan` / `2. Lieque` with
 * *"Reply with the number"* under it (`questionText` in `packages/messaging/src/choice.ts`),
 * and turning those into an unordered list renumbers the learner's own answer — they read
 * "the third one", type 3, and are marked wrong. The three characters here cannot appear
 * at the head of a line for any other reason; a digit can.
 *
 * The space is required. `*emphasis*` at the start of a paragraph is not a list.
 */
const BULLET_LINE = /^[-*•][ \t]+(.*)$/;
/**
 * A step line: `1) `, up to three digits, and the closing bracket is required.
 *
 * See `BRIEFING_STEP` for why it is not `1. `. Three digits because a briefing of 300
 * words has no hundredth step and a four-digit run at the head of a line is a year.
 */
const STEP_LINE = /^(\p{N}{1,3})\)[ \t]+(.*)$/u;
/**
 * A table row: fenced at both ends, with at least one cell between the fences.
 *
 * Anchored at both ends rather than only the front, which is what keeps it from claiming
 * prose. A sentence beginning with a pipe is already vanishingly rare; one that also ends
 * with a pipe is a row somebody meant as a row.
 */
const TABLE_LINE = /^\|(.*)\|$/;
/**
 * A cell that is a Markdown alignment rule rather than a fact: `---`, `:---`, `---:`.
 *
 * **`Prose` runs on every message and not only on briefings**, which is the whole reason
 * this is here. `projectBriefing` never writes one — but a grading reply, an elaboration and
 * anything a learner pastes are free text, and nothing in those prompts forbids Markdown. A
 * model that writes a Markdown table writes the rule row under its headers, and without this
 * it renders as a row of dashes: a fact that is not one, sitting in the middle of the grid.
 */
const RULE_CELL = /^:?-{3,}:?$/;
/**
 * One block's text, flattened to a single line with no marker of its own.
 *
 * Four things, all of which keep `parseBriefing(projectBriefing(blocks))` equal to
 * `blocks` — a round trip that has to hold, because the projection is the only copy
 * anything downstream ever sees. A newline inside a block would re-parse as a block
 * boundary, so a model that writes a paragraph with a line break in it must not silently
 * gain a paragraph. And a model that writes its own marker in front of a block's text must
 * not produce `- - the point`, which is what happens if the marker is prepended blindly —
 * the same hazard for `1) 1) rinse` and for a paragraph that happens to be fenced with
 * pipes at both ends, which would come back as a one-cell table nobody wrote.
 *
 * The pipe case drops the leading fence rather than the text, because the text is the
 * briefing and the fence is ours: `| a | b |` kept as `a | b |` still reads as what the
 * model typed and no longer matches `TABLE_LINE`.
 */
function oneLine(value) {
    if (typeof value !== 'string')
        return '';
    const flat = value.replace(/\s+/g, ' ').trim();
    const heading = HEADING_LINE.exec(flat);
    if (heading)
        return (heading[1] ?? '').trim();
    const bullet = BULLET_LINE.exec(flat);
    if (bullet)
        return (bullet[1] ?? '').trim();
    const step = STEP_LINE.exec(flat);
    if (step)
        return (step[2] ?? '').trim();
    if (TABLE_LINE.test(flat))
        return flat.slice(1).trim();
    return flat;
}
/**
 * One cell, flattened and with its own pipes taken out.
 *
 * Not `oneLine`, deliberately: a cell is not a line and stripping a marker from it would
 * be wrong — `1) rinse` is a legitimate cell, and so is a cell that begins with a dash.
 * The one character it may not contain is the separator, and a cell carrying one is
 * rewritten rather than refused, because losing the row to punctuation is worse than a
 * slash where the model typed a pipe. That is the same trade `oneLine` already makes with
 * a newline.
 */
function cell(value) {
    if (typeof value !== 'string')
        return '';
    return value.replace(/\s+/g, ' ').replace(/\|/g, '/').trim();
}
/**
 * The table rows of a projected briefing, as they were written.
 *
 * **Exported because two quote checks need to recognise one**, and a second definition of
 * the row format living in `packages/ingest` is exactly what this file exists to prevent —
 * the same argument `BRIEFING_BULLET` is exported for. `isGroundedIn` and `quotesBriefing`
 * both impose a minimum length on a quote, sized for a sentence, and a two-column row is
 * shorter than any sentence: `| LU 7 | Lung |` is seven characters once punctuation is
 * stripped, against a floor of twelve. Both would discard a perfectly good quote — one
 * dropping a question about the table, the other dropping an objection *to* it — so both
 * ask this which lines of the briefing are rows, and exempt a quote that is a whole one.
 *
 * A row is a complete claim whatever its length, which is the whole reason the exemption is
 * safe: it is exact equality against a line the briefing really contains, not a loosening of
 * the floor for every short string.
 */
export function briefingRows(text) {
    const rows = [];
    for (const line of text.split('\n')) {
        const trimmed = line.trim();
        if (TABLE_LINE.test(trimmed) && cellsOf(trimmed).some((value) => value !== '')) {
            rows.push(trimmed);
        }
    }
    return rows;
}
/** The cells between the fences, in order, empties included. See `BRIEFING_CELL`. */
function cellsOf(line) {
    const inner = TABLE_LINE.exec(line)?.[1];
    if (inner === undefined)
        return [];
    return inner.split('|').map((part) => part.trim());
}
/** One row as the projection writes it: `| a | b | c |`. */
function rowLine(cells) {
    return `${BRIEFING_CELL} ${cells.join(` ${BRIEFING_CELL} `)} ${BRIEFING_CELL}`;
}
/**
 * The blocks as the one string everything downstream stores, sends and shows.
 *
 * Empty blocks are dropped rather than emitted, because a blank bullet renders as a
 * bullet with nothing beside it and reads as the briefing having lost a line. A table
 * whose every cell is empty is the same thing one dimension up: a grid of nothing reads
 * as a picture that failed to load, which is the impression this whole feature exists
 * not to give.
 *
 * **The steps are renumbered from one here rather than trusted**, which is the point of
 * the number being ours. A model that returns a sequence starting at 3, or one whose
 * fourth entry is also numbered 3, would otherwise teach the wrong order in the one block
 * kind whose order *is* the content.
 *
 * **The round trip is identity for canonical blocks and normalising for the rest**, which
 * is worth stating rather than leaving to be discovered. A `table` whose `headers` are all
 * blank loses that line to the same filter the body rows get, so its first body row comes
 * back as the header row — nothing is lost, and re-projecting the result is stable, but it
 * is not the object that went in. `rowsOf` in `items.ts` already drops blank rows, so the
 * generation path cannot construct one; a caller building a block by hand can, and this is
 * what happens when they do. It is the same class of normalisation as an empty bullet being
 * dropped, which this function has always done.
 */
export function projectBriefing(blocks) {
    const rendered = [];
    for (const block of blocks) {
        if (block.kind === 'bullets') {
            const items = block.items.map(oneLine).filter((item) => item !== '');
            if (items.length > 0)
                rendered.push(items.map((item) => `${BRIEFING_BULLET}${item}`).join('\n'));
            continue;
        }
        if (block.kind === 'steps') {
            const items = block.items.map(oneLine).filter((item) => item !== '');
            if (items.length > 0) {
                rendered.push(items.map((item, at) => `${at + 1}${BRIEFING_STEP}${item}`).join('\n'));
            }
            continue;
        }
        if (block.kind === 'table') {
            const lines = [block.headers, ...block.rows]
                .map((row) => row.map(cell))
                .filter((row) => row.some((value) => value !== ''))
                .map(rowLine);
            if (lines.length > 0)
                rendered.push(lines.join('\n'));
            continue;
        }
        const text = oneLine(block.text);
        if (text === '')
            continue;
        rendered.push(block.kind === 'heading' ? `${BRIEFING_HEADING}${text}` : text);
    }
    return rendered.join('\n\n');
}
/**
 * The stored string, read back as blocks.
 *
 * Total on every input, which is the property that matters: this runs on rows nobody
 * designed for it — 125 live briefings, every question, every correction, the daily
 * summary and whatever a learner typed — and a chat that throws on one of them shows no
 * conversation at all.
 *
 * **A non-empty string never parses to nothing.** Anything that produced no block at all
 * comes back as one paragraph holding the text unchanged, so the worst case is exactly the
 * behaviour this replaces rather than an empty bubble.
 */
export function parseBriefing(text) {
    const blocks = [];
    let paragraph = [];
    let bullets = [];
    let steps = [];
    let rows = [];
    const closeParagraph = () => {
        if (paragraph.length > 0)
            blocks.push({ kind: 'paragraph', text: paragraph.join('\n') });
        paragraph = [];
    };
    const closeBullets = () => {
        if (bullets.length > 0)
            blocks.push({ kind: 'bullets', items: bullets });
        bullets = [];
    };
    const closeSteps = () => {
        if (steps.length > 0)
            blocks.push({ kind: 'steps', items: steps });
        steps = [];
    };
    /*
     * The first row of a run is the header row, and a run of one is headers with no body.
     *
     * There is no separator line under the headers and deliberately never will be. A
     * `| --- | --- |` rule is Markdown's way of saying which row is the header, and it is a
     * second thing that has to be written correctly, read correctly and kept in step — with
     * nothing to show for it, because position already says it. A run's first row is its
     * headers in every table anybody has ever drawn.
     */
    const closeTable = () => {
        if (rows.length > 0)
            blocks.push({ kind: 'table', headers: rows[0] ?? [], rows: rows.slice(1) });
        rows = [];
    };
    /** Everything but the run this line belongs to. */
    const closeOthers = (keep) => {
        closeParagraph();
        if (keep !== 'bullets')
            closeBullets();
        if (keep !== 'steps')
            closeSteps();
        if (keep !== 'table')
            closeTable();
    };
    for (const line of text.split('\n')) {
        const trimmed = line.trim();
        if (trimmed === '') {
            closeOthers();
            continue;
        }
        const heading = HEADING_LINE.exec(trimmed);
        if (heading) {
            closeOthers();
            const content = (heading[1] ?? '').trim();
            // A line of nothing but hashes is not a heading with an empty name, it is a rule
            // somebody typed. Kept as the paragraph text it looks like rather than dropped.
            if (content === '')
                paragraph.push(trimmed);
            else
                blocks.push({ kind: 'heading', text: content });
            continue;
        }
        const bullet = BULLET_LINE.exec(trimmed);
        if (bullet) {
            closeOthers('bullets');
            const content = (bullet[1] ?? '').trim();
            if (content === '')
                paragraph.push(trimmed);
            else
                bullets.push(content);
            continue;
        }
        const step = STEP_LINE.exec(trimmed);
        if (step) {
            closeOthers('steps');
            const content = (step[2] ?? '').trim();
            // `7)` on its own is not a step with no words in it. Same reading as a bare `-`.
            if (content === '')
                paragraph.push(trimmed);
            else
                steps.push(content);
            continue;
        }
        if (TABLE_LINE.test(trimmed)) {
            const cells = cellsOf(trimmed);
            const empty = !cells.some((value) => value !== '');
            const rule = !empty && cells.every((value) => RULE_CELL.test(value));
            if (!empty && !rule) {
                closeOthers('table');
                rows.push(cells);
                continue;
            }
            /*
             * A blank or rule-only fenced line **inside a run is skipped and keeps the run open**,
             * which is not the same as ignoring it and matters more than it looks.
             *
             * The first draft let it fall through to the paragraph branch, and the paragraph
             * branch closes the table. So `|  |  |` in the middle of a grid split the table in
             * two, rendered its pipes as prose between the halves, and **promoted the first row of
             * the remainder to a column heading** — a fact shown as the name of a column. That is
             * considerably worse than the gap the guard was written to avoid.
             *
             * Outside a run it still falls through, so `||` typed by a learner is the paragraph it
             * looks like rather than a table with nothing in it.
             */
            if (rows.length > 0)
                continue;
        }
        /*
         * A paragraph line closes a run of bullets, so prose under a list is prose and not a
         * fourth bullet with no marker. The line keeps its own place in the paragraph — the
         * lines are joined with `\n` and not with a space — because a question's options are
         * one per line and joining them would run them together.
         */
        closeBullets();
        closeSteps();
        closeTable();
        paragraph.push(trimmed);
    }
    closeOthers();
    if (blocks.length === 0 && text !== '')
        return [{ kind: 'paragraph', text }];
    return blocks;
}
//# sourceMappingURL=briefing.js.map